import React, { ReactNode, useEffect, useState } from 'react';
import * as RuiAccordion from '@radix-ui/react-accordion';
import { ChevronDown, ChevronUp } from 'react-feather';
import classNames from 'classnames';

import { createStyles } from '../../../utils/createStyle';

const styles = createStyles({
    accordionGroupHeader: 'text-grey-1',
    accordionGroupButton: [
        'p-3 block w-full text-left hover:text-brand flex justify-between items-center',
    ],
    root: 'max-w-full',
    title: 'inline-block align-top mr-2',
    content: ['border-b border-solid border-grey-light', { paddingBottom: '20px' }],
});

export interface AccordionItem {
    value: string;
    title: ReactNode;
    content: ReactNode;
}

export interface AccordionProps {
    items: AccordionItem[];
    defaultExpandedItemValue?: string;
    multipleEntriesOpen?: boolean;
}

export const Accordion = ({
    items,
    defaultExpandedItemValue,
    multipleEntriesOpen = false,
}: AccordionProps) => {
    const [expandedItem, setExpandedItem] = useState(defaultExpandedItemValue);
    const [multiSelectExpandedItems, setMultiSelectExpandedItems] = useState<string[]>([]);

    // effect for keeping expandedItem up
    // to date as the user change tabs
    useEffect(() => {
        setExpandedItem(defaultExpandedItemValue);
    }, [defaultExpandedItemValue]);

    return (
        <RuiAccordion.Root
            type={(multipleEntriesOpen ? 'multiple' : 'single') as any}
            value={expandedItem}
            onValueChange={(value: any) => {
                if (Array.isArray(value)) {
                    setMultiSelectExpandedItems(value);
                } else {
                    setExpandedItem(value);
                }
            }}
            className={styles.root}
        >
            {items.map(({ title, value, content }) => (
                <RuiAccordion.Item key={value} value={value}>
                    <RuiAccordion.Header className={styles.accordionGroupHeader}>
                        <RuiAccordion.Button
                            className={classNames(
                                styles.accordionGroupButton,
                                expandedItem !== value
                                    ? 'border-b border-solid border-grey-light'
                                    : 'text-brand'
                            )}
                        >
                            {title}
                            <span className={styles.title}>
                                {(
                                    multipleEntriesOpen
                                        ? multiSelectExpandedItems.includes(value)
                                        : expandedItem === value
                                ) ? (
                                    <ChevronUp />
                                ) : (
                                    <ChevronDown />
                                )}
                            </span>
                        </RuiAccordion.Button>

                        <RuiAccordion.Panel className={classNames(styles.content)}>
                            {content}
                        </RuiAccordion.Panel>
                    </RuiAccordion.Header>
                </RuiAccordion.Item>
            ))}
        </RuiAccordion.Root>
    );
};
